import { Redirect } from 'expo-router';
import type { ReactNode } from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { Bounded } from '@/components/ui/Bounded';
import { BrandMark } from '@/components/ui/BrandMark';
import { Screen } from '@/components/ui/Screen';
import { Text } from '@/components/ui/Text';
import { useAuth } from '@/lib/auth';
import { peekPendingJoin } from '@/lib/pending-join';

type AuthScreenProps = {
  title: string;
  subtitle: string;
  children: ReactNode;
};

export function AuthScreen({ title, subtitle, children }: AuthScreenProps) {
  const { session } = useAuth();

  // Already authenticated → resume a pending share-link redeem, else go home.
  // Peek (don't consume) so that if both the sign-in and sign-up screens are
  // mounted, they resolve the same destination; JoinScreen clears the token.
  if (session) {
    const pending = peekPendingJoin();
    const href = pending ? `/join/template/${pending.token}` : '/';
    return <Redirect href={href} />;
  }

  return (
    <Screen>
      <KeyboardAwareScrollView
        bottomOffset={60}
        keyboardShouldPersistTaps="handled"
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
      >
        <Bounded className="gap-6 px-6">
          <BrandMark size="lg" className="self-center" />

          <View className="gap-1">
            <Text variant="title" className="text-center">
              {title}
            </Text>
            <Text variant="subtitle" className="text-center">
              {subtitle}
            </Text>
          </View>

          {children}
        </Bounded>
      </KeyboardAwareScrollView>
    </Screen>
  );
}
